import { Injectable } from "@angular/core";
import { Config } from 'src/environments/environment';
import { UserData } from "../models/user-data";
import { BehaviorSubject } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";
import { ActivatedRoute, Router } from "@angular/router";

@Injectable({ providedIn: 'root' })
export class AuthService {

  constructor(
    private router: Router,
    private route: ActivatedRoute
  ){}

  /** Blocks AppComponent auto-redirect after clearSession (e.g. 401/403 or sign-out). */
  private suppressAutoLogin = false;

  get autoLoginSuppressed(): boolean {
    return this.suppressAutoLogin;
  }

  private userDataSubject: BehaviorSubject<UserData> = new BehaviorSubject({
    sessionUID: localStorage.getItem('sessionId'),
    userName: localStorage.getItem('userName'),
    companyName: localStorage.getItem('companyName'),
    functionalGroups: localStorage.getItem('functionalGroups')?.split(',') ?? [],
    isLoggedIn: !!localStorage.getItem('sessionId'),
    firstName: localStorage.getItem('firstName'),
    lastName: localStorage.getItem('lastName'),
  } as UserData)
  public userData$ = this.userDataSubject.asObservable().pipe(distinctUntilChanged());

  public isLoggedIn$ = this.userData$.pipe(
    map(userData => !!userData.sessionUID || !!localStorage.getItem('sessionId'))
  )

  public sessionUid$ = this.userData$.pipe(
    map(userData => userData.sessionUID ?? localStorage.getItem('sessionId'))
  )

  clearSession(): void {
    this.suppressAutoLogin = true;
    this.userDataSubject.next({userName: '', sessionUID: '', isLoggedIn: false, companyName: '', functionalGroups: [], firstName: '', lastName: ''})

    // Clear data from local storage
    localStorage.removeItem('sessionId');
    localStorage.removeItem('userName');
    localStorage.removeItem('companyName');
    localStorage.removeItem('functionalGroups');
    localStorage.removeItem('firstName');
    localStorage.removeItem('lastName');
  }

  /**
	 * Performs login.
	 */
	public login() {
    this.suppressAutoLogin = false;
		const host = encodeURIComponent(window.location.href);
		const loginUrl = `${Config.colaConfig.redirectUrl}?origin=${host}`;
		// this.loggerService.flush();
		document.location.href = loginUrl;
	}

  public updateSession(sessionId: string, username: string, company: string, functionalGroups: string[], firstName: string, lastName: string) {
    this.suppressAutoLogin = false;
		const existingSessionId: string = localStorage.getItem('sessionId') ?? '';
		const existingUsername: string = localStorage.getItem('userName') ?? '';
		const existingCompany: string = localStorage.getItem('companyName') ?? '';
		const existingFunctionalGroups: string[] = localStorage.getItem('functionalGroups')?.split(',') ?? [];

		if (existingCompany !== company || existingUsername !== username || existingSessionId !== sessionId || this.areArraisDifferent(functionalGroups, existingFunctionalGroups)) {
			localStorage.setItem('sessionId', sessionId);
			localStorage.setItem('userName', username);
			localStorage.setItem('companyName', company);
			localStorage.setItem('functionalGroups', functionalGroups.join(','));
			localStorage.setItem('firstName', firstName);
			localStorage.setItem('lastName', lastName);

			this.userDataSubject.next({
				sessionUID: sessionId,
				userName: username,
				companyName: company,
        functionalGroups: functionalGroups,
				isLoggedIn: true,
				firstName: firstName,
				lastName: lastName,
			} as UserData);
		} else {
			this.removeSpecificQueryParams(['sessionId', 'username', 'company', 'functionalGroups', 'fenixOneTimeToken']);
		}
	}

  private areArraisDifferent(array1: string[], array2: string[]) {
    if (array1.length !== array2.length)
      return true;

    const aSorted = [...array1].sort();
    const bSorted = [...array2].sort();

    return aSorted.some((v, i) => v !== bSorted[i]);
  }

  /**
   * Reads a query param from the router, pre-hash search, or hash query (hash routing).
   */
  getQueryParam(name: string): string | null {
    const fromRoute = this.route.snapshot.queryParamMap.get(name);
    if (fromRoute) {
      return fromRoute;
    }

    const fromSearch = new URLSearchParams(window.location.search).get(name);
    if (fromSearch) {
      return fromSearch;
    }

    const hash = window.location.hash;
    const hashQueryStart = hash.indexOf('?');
    if (hashQueryStart === -1) {
      return null;
    }
    return new URLSearchParams(hash.slice(hashQueryStart + 1)).get(name);
  }

  /**
   * Removes specified query parameters from route.
   * Used to clear parameters related to authorization after use to prevent loops.
   * @param paramsToRemove Array of query params to remove.
   */
  public removeSpecificQueryParams(paramsToRemove: string[]) {
    // Get the current query parameters
    const queryParams = { ...this.route.snapshot.queryParams };

    // Remove specific parameters
    paramsToRemove.forEach(param => delete queryParams[param]);

    // Construct a new URL without the removed parameters
    const urlTree = this.router.createUrlTree([], { queryParams });

    // Navigate to the new URL
    this.router.navigateByUrl(urlTree);

    const search = new URLSearchParams(window.location.search);
    let searchChanged = false;
    paramsToRemove.forEach(param => {
      if (search.has(param)) {
        search.delete(param);
        searchChanged = true;
      }
    });
    if (searchChanged) {
      const q = search.toString();
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${q ? `?${q}` : ''}${window.location.hash}`,
      );
    }
  }

}
